import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Search,
  ArrowLeft,
  Scale,
  Gavel,
  FileText,
  BookOpen,
  Filter,
  Calendar,
  Building2,
  ChevronRight,
  ChevronDown,
  Loader2,
  Star,
  TrendingUp,
  AlertCircle,
  Clock,
  Download,
  Sparkles,
  ExternalLink
} from 'lucide-react';

const JurisprudencePage = () => {
  const { api } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('search'); // search, trending, recent
  const [query, setQuery] = useState('');
  const [area, setArea] = useState('');
  const [court, setCourt] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [areas, setAreas] = useState([]);
  const [courts, setCourts] = useState([]);
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [recentCases, setRecentCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [semanticSearch, setSemanticSearch] = useState(false);
  
  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);
  
  const loadInitialData = async () => {
    try {
      const [areasRes, courtsRes, trendingRes, recentRes, statsRes] = await Promise.all([
        api.get('/jurisprudence/areas'),
        api.get('/jurisprudence/courts'),
        api.get('/jurisprudence/trending-topics'),
        api.get('/jurisprudence/recent?limit=5'),
        api.get('/jurisprudence/statistics')
      ]);
      
      setAreas(areasRes.data.areas || []);
      setCourts(courtsRes.data.courts || []);
      setTrendingTopics(trendingRes.data.trending_topics || []);
      setRecentCases(recentRes.data.recent_cases || []);
      setStatistics(statsRes.data.statistics || {});
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };
  
  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error('Ingresa un término de búsqueda');
      return;
    }
    
    setLoading(true);
    setAiAnalysis(null);
    try {
      const params = new URLSearchParams({ query, limit: '15' });
      if (area) params.append('area', area);
      if (court) params.append('court', court);
      if (semanticSearch) params.append('semantic', 'true');
      
      const response = await api.get(`/jurisprudence/search?${params.toString()}`);
      setResults(response.data.results || []);
      
      if (response.data.results?.length === 0) {
        toast.info('No se encontraron resultados');
      } else if (semanticSearch) {
        toast.success(`Búsqueda semántica: ${response.data.results.length} resultados`);
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Error en la búsqueda');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAnalyze = async () => {
    if (results.length === 0) {
      toast.error('Primero realiza una búsqueda');
      return;
    }
    
    setAnalyzing(true);
    try {
      const response = await api.post('/jurisprudence/analyze', {
        query: query,
        case_ids: results.slice(0, 5).map(r => r.id)
      });
      setAiAnalysis(response.data.ai_analysis || {});
      toast.success('Análisis completado');
    } catch (error) {
      console.error('Error analyzing:', error);
      toast.error('Error en el análisis');
    } finally {
      setAnalyzing(false);
    }
  };
  
  const getCourtColor = (court) => {
    const colors = {
      corte_suprema: 'purple',
      corte_apelaciones: 'blue',
      tribunal_constitucional: 'red',
      tribunal_tributario: 'amber',
      juzgado_laboral: 'green',
      juzgado_civil: 'slate',
      juzgado_familia: 'pink'
    };
    return colors[court] || 'slate';
  };
  
  const getAreaIcon = (area) => {
    const icons = {
      laboral: Gavel,
      tributario: Building2,
      constitucional: Scale,
      civil: FileText,
      familia: BookOpen
    };
    return icons[area] || FileText;
  };
  
  return (
    <div className="min-h-screen bg-background" data-testid="jurisprudence-page">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <Gavel className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Búsqueda de Jurisprudencia</h1>
                <p className="text-xs text-slate-400">Sentencias y precedentes chilenos</p>
              </div>
            </div>
          </div>
          <Link to="/expertos-notariales" data-testid="jurisprudence-experts-link" className="text-sm font-semibold rounded-full border border-amber-500/50 text-amber-300 px-4 py-2 hover:bg-amber-500/10">Expertos notariales →</Link>
          
          {statistics && (
            <div className="hidden md:flex items-center gap-4 text-xs text-slate-400">
              <span>{statistics.total_cases} casos en base de datos</span>
            </div>
          )}
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search Bar */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Buscar jurisprudencia... (ej: despido injustificado, recurso de protección)"
                data-testid="search-input"
                className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-purple-500 focus:outline-none"
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              <select
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className="px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-purple-500 focus:outline-none"
              >
                <option value="">Todas las áreas</option>
                {areas.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              
              <select
                value={court}
                onChange={(e) => setCourt(e.target.value)}
                className="px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-purple-500 focus:outline-none"
              >
                <option value="">Todos los tribunales</option>
                {courts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              
              <button
                onClick={handleSearch}
                disabled={loading}
                data-testid="search-btn"
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-xl font-medium transition-colors flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                Buscar
              </button>
            </div>
          </div>
          
          {/* Semantic Search Toggle */}
          <div className="mt-4 flex items-center justify-between">
            <label className="flex items-center gap-3 cursor-pointer">
              <div 
                onClick={() => setSemanticSearch(!semanticSearch)}
                className={`w-12 h-6 rounded-full transition-colors relative ${semanticSearch ? 'bg-purple-600' : 'bg-slate-700'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${semanticSearch ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className={`w-4 h-4 ${semanticSearch ? 'text-purple-400' : 'text-slate-500'}`} />
                <span className={`text-sm ${semanticSearch ? 'text-white' : 'text-slate-400'}`}>
                  Búsqueda semántica con IA
                </span>
              </div>
            </label>
            <p className="text-xs text-slate-500">
              {semanticSearch ? 'La IA entiende el contexto de tu búsqueda' : 'Búsqueda por palabras clave'}
            </p>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Results */}
          <div className="lg:col-span-2 space-y-4">
            {results.length > 0 ? (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-white">{results.length} resultados encontrados</h3>
                  <button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center gap-2"
                  >
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Analizar con IA
                  </button>
                </div>
                
                {results.map((caseItem, index) => {
                  const color = getCourtColor(caseItem.court);
                  const AreaIcon = getAreaIcon(caseItem.area);
                  
                  return (
                    <div
                      key={caseItem.id}
                      className="bg-card border border-border rounded-xl p-5 hover:border-purple-500/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedCase(selectedCase?.id === caseItem.id ? null : caseItem)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${color}-500/20 text-${color}-400`}>
                              {courts.find(c => c.id === caseItem.court)?.name || caseItem.court}
                            </span>
                            <span className="text-xs text-slate-500">{caseItem.date}</span>
                          </div>
                          <h4 className="font-semibold text-white mb-1">{caseItem.subject}</h4>
                          <p className="text-sm text-slate-400 mb-2">{caseItem.rol}</p>
                          <p className="text-sm text-slate-300">{caseItem.summary}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${color}-500/10`}>
                            <AreaIcon className={`w-5 h-5 text-${color}-400`} />
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber-400" />
                            <span className="text-xs text-slate-400">{caseItem.relevance_score || caseItem.search_score}%</span>
                          </div>
                        </div>
                      </div>
                      
                      {selectedCase?.id === caseItem.id && (
                        <div className="mt-4 pt-4 border-t border-slate-700 space-y-3">
                          <div>
                            <p className="text-xs text-slate-400 mb-1">Partes</p>
                            <p className="text-sm text-white">{caseItem.parties}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 mb-1">Holding / Doctrina</p>
                            <p className="text-sm text-slate-300">{caseItem.holding}</p>
                          </div>
                          {caseItem.legal_basis && caseItem.legal_basis.length > 0 && (
                            <div>
                              <p className="text-xs text-slate-400 mb-1">Base Legal</p>
                              <div className="flex flex-wrap gap-1">
                                {caseItem.legal_basis.map((basis, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded text-xs font-medium">
                                    {basis}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-slate-400 mb-1">Palabras clave</p>
                            <div className="flex flex-wrap gap-1">
                              {caseItem.keywords?.map((kw, i) => (
                                <span key={i} className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded text-xs">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <Gavel className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-slate-400 mb-2">Busca Jurisprudencia</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Ingresa términos como "despido injustificado", "prescripción tributaria" o "recurso de protección"
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['despido', 'prescripción', 'tutela laboral', 'alimentos'].map(term => (
                    <button
                      key={term}
                      onClick={() => { setQuery(term); handleSearch(); }}
                      className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded-full text-sm text-slate-300 transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* AI Analysis */}
            {aiAnalysis?.analysis && (
              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-6">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-400" />
                  Análisis de Jurisprudencia con IA
                </h4>
                <div className="prose prose-invert prose-sm max-w-none">
                  <div className="text-slate-300 whitespace-pre-wrap">{aiAnalysis.analysis}</div>
                </div>
                <p className="text-xs text-slate-500 mt-4">
                  Generado el {new Date(aiAnalysis.generated_at).toLocaleString('es-CL')} • {aiAnalysis.cases_analyzed} casos analizados
                </p>
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-4">
            {/* Trending Topics */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-400" />
                Temas Tendencia
              </h4>
              <div className="space-y-3">
                {trendingTopics.map((topic, index) => (
                  <button
                    key={index}
                    onClick={() => { setQuery(topic.topic); setArea(topic.area); }}
                    className="w-full text-left p-3 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors relative"
                  >
                    {topic.hot && (
                      <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded">
                        HOT
                      </span>
                    )}
                    <p className="font-medium text-white text-sm flex items-center gap-2">
                      {topic.topic}
                      {topic.hot && <span className="text-red-400 text-xs">🔥</span>}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{topic.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {topic.related_articles?.map((art, i) => (
                        <span key={i} className="text-xs text-purple-400">{art}</span>
                      ))}
                    </div>
                    {topic.recent_cases && (
                      <p className="text-[10px] text-slate-500 mt-1">{topic.recent_cases} casos recientes</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Recent Cases */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Casos Recientes
              </h4>
              <div className="space-y-3">
                {recentCases.map((caseItem, index) => (
                  <div key={index} className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="font-medium text-white text-sm truncate">{caseItem.subject}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-400">{caseItem.rol}</span>
                      <span className="text-xs text-slate-500">•</span>
                      <span className="text-xs text-slate-400">{caseItem.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Quick Stats */}
            {statistics && (
              <div className="bg-card border border-border rounded-xl p-5">
                <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Scale className="w-5 h-5 text-green-400" />
                  Estadísticas
                </h4>
                <div className="text-center mb-4">
                  <p className="text-3xl font-bold text-white">{statistics.total_cases}</p>
                  <p className="text-xs text-slate-400">casos en base de datos</p>
                </div>
                <div className="space-y-2">
                  {Object.entries(statistics.by_area || {}).slice(0, 5).map(([area, count]) => (
                    <div key={area} className="flex items-center justify-between text-sm">
                      <span className="text-slate-400 capitalize">{area}</span>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                  ))}
                </div>
                {statistics.date_range && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <p className="text-xs text-slate-500">
                      Casos desde {statistics.date_range.oldest} hasta {statistics.date_range.newest}
                    </p>
                  </div>
                )}
                <p className="text-xs text-green-400 mt-3 text-center font-medium">
                  Base de datos expandida con IA
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default JurisprudencePage;
